import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const ManageWarehouses = () => {
  const [warehouses, setWarehouses] = useState([])
  const [filteredWarehouses, setFilteredWarehouses] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: ''
  })

  const applyFilters = useCallback(() => {
    let filtered = [...warehouses]

    // Search filter (name, location)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(warehouse =>
        warehouse.name?.toLowerCase().includes(searchLower) ||
        warehouse.location?.toLowerCase().includes(searchLower)
      )
    }

    setFilteredWarehouses(filtered)
  }, [warehouses, filters])

  const {
    paginatedItems: paginatedWarehouses,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredWarehouses)

  useEffect(() => {
    fetchWarehouses()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const fetchWarehouses = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      const warehousesData = response.data.results || response.data || []
      setWarehouses(warehousesData)
      setFilteredWarehouses(warehousesData)
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch warehouses. Please try again.',
        confirmButtonColor: '#d33'
      })
    } finally {
      setLoading(false)
    }
  }

  const updateWarehouse = async (warehouseId, formData) => {
    try {
      // Prepare payload according to backend expectations
      const payload = {
        name: formData.name,
        location: formData.location || ''
      }

      // Use PUT method as per backend code
      await apiClient.put(`${API_ENDPOINTS.WAREHOUSES}${warehouseId}/`, payload)

      // Refresh warehouses list
      await fetchWarehouses()

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Warehouse updated successfully',
        confirmButtonColor: '#3085d6',
        timer: 2000,
        showConfirmButton: true
      })
    } catch (error) {
      console.error('Error updating warehouse:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.response?.data?.error || error.response?.data?.detail || 'Failed to update warehouse. Please try again.',
        confirmButtonColor: '#d33'
      })
      throw error
    }
  }

  const editWarehouse = async (warehouse) => {
    const editFormHtml = `
      <form id="editWarehouseForm" style="text-align: left; max-width: 100%; box-sizing: border-box; overflow: hidden;">
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Warehouse Name <span style="color: red;">*</span></label>
          <input type="text" id="edit_name" class="swal2-input" value="${warehouse.name || ''}" required style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; font-size: 14px;">
        </div>
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Location</label>
          <input type="text" id="edit_location" class="swal2-input" value="${warehouse.location || ''}" style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; font-size: 14px;">
        </div>
      </form>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 16px; font-weight: 600;">Edit Warehouse</span>',
      html: editFormHtml,
      width: '450px',
      padding: '1.5rem',
      showCancelButton: true,
      confirmButtonText: 'Update Warehouse',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'swal2-popup-edit',
        htmlContainer: 'swal2-html-container-edit'
      },
      didOpen: () => {
        const popup = document.querySelector('.swal2-popup')
        if (popup) {
          popup.style.maxWidth = '450px'
          popup.style.overflow = 'hidden'
          popup.style.boxSizing = 'border-box'
        }

        const htmlContainer = document.querySelector('.swal2-html-container')
        if (htmlContainer) {
          htmlContainer.style.maxWidth = '100%'
          htmlContainer.style.overflow = 'hidden'
          htmlContainer.style.boxSizing = 'border-box'
          htmlContainer.style.padding = '0'
        }

        const form = document.getElementById('editWarehouseForm')
        if (form) {
          form.style.maxWidth = '100%'
          form.style.overflow = 'hidden'
        }

        const inputs = document.querySelectorAll('#editWarehouseForm input')
        inputs.forEach(input => {
          input.style.maxWidth = '100%'
          input.style.boxSizing = 'border-box'
        })
      },
      preConfirm: async () => {
        const formData = {
          name: document.getElementById('edit_name').value.trim(),
          location: document.getElementById('edit_location').value.trim()
        }

        // Validation
        if (!formData.name) {
          Swal.showValidationMessage('Warehouse name is required')
          return false
        }

        return formData
      }
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        try {
          await updateWarehouse(warehouse.id, result.value)
        } catch (error) {
          // Error already handled in updateWarehouse
        }
      }
    })
  }

  const handleDelete = async (warehouseId, warehouseName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete warehouse "${warehouseName}"? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    })

    if (result.isConfirmed) {
      try {
        await apiClient.delete(`${API_ENDPOINTS.WAREHOUSES}${warehouseId}/`)

        // Remove warehouse from state
        setWarehouses(prevWarehouses => prevWarehouses.filter(warehouse => warehouse.id !== warehouseId))
        setFilteredWarehouses(prevFiltered => prevFiltered.filter(warehouse => warehouse.id !== warehouseId))

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Warehouse has been deleted successfully.',
          confirmButtonColor: '#3085d6',
          timer: 2000,
          showConfirmButton: true
        })
      } catch (error) {
        console.error('Error deleting warehouse:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to delete warehouse. Please try again.',
          confirmButtonColor: '#d33'
        })
      }
    }
  }

  const showWarehouseDetails = (warehouse) => {
    const detailsHtml = `
      <div style="text-align: left; padding: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 8px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin-right: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
            <i class="bi bi-building"></i>
          </div>
          <div>
            <h3 style="margin: 0; color: #2c3e50; font-weight: 600; font-size: 16px;">${warehouse.name || 'N/A'}</h3>
            <p style="margin: 2px 0 0 0; color: #7f8c8d; font-size: 12px;">Warehouse ID: ${warehouse.id || 'N/A'}</p>
          </div>
        </div>
        
        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
          <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Location</div>
          <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${warehouse.location || 'N/A'}</div>
        </div>
      </div>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 18px; font-weight: 600;">Warehouse Details</span>',
      html: detailsHtml,
      icon: 'info',
      showDenyButton: true,
      showCloseButton: true,
      confirmButtonText: '<i class="bi bi-pencil"></i> Edit Warehouse',
      denyButtonText: '<i class="bi bi-trash"></i> Delete Warehouse',
      confirmButtonColor: '#3085d6',
      denyButtonColor: '#d33',
      width: '550px',
      heightAuto: false,
      padding: '1rem',
      customClass: {
        popup: 'swal2-popup-custom'
      },
      footer: '<button id="swal-close-btn" class="btn btn-secondary" style="margin-top: 10px; padding: 8px 16px; border: none; border-radius: 5px;">Close</button>',
      didOpen: () => {
        const confirmButton = Swal.getConfirmButton()
        const denyButton = Swal.getDenyButton()
        const closeButton = document.getElementById('swal-close-btn')

        if (confirmButton) {
          confirmButton.style.border = 'none'
          confirmButton.style.padding = '10px 20px'
          confirmButton.style.borderRadius = '5px'
          confirmButton.style.fontWeight = '500'
          confirmButton.style.marginRight = '10px'
        }

        if (denyButton) {
          denyButton.style.border = 'none'
          denyButton.style.padding = '10px 20px'
          denyButton.style.borderRadius = '5px'
          denyButton.style.fontWeight = '500'
          denyButton.style.marginRight = '10px'
        }

        if (closeButton) {
          closeButton.onclick = () => Swal.close()
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Edit warehouse
        await editWarehouse(warehouse)
      } else if (result.isDenied) {
        // Delete warehouse
        await handleDelete(warehouse.id, warehouse.name)
      }
    })
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const clearFilters = () => {
    setFilters({
      search: ''
    })
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Warehouses</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Warehouses</li>
            <li className="breadcrumb-item active">Manage Warehouses</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    <i className="bi bi-building me-2"></i>
                    Manage Warehouses
                  </h5>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={fetchWarehouses}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                  </button>
                </div>
                <p className="text-muted">View and manage all warehouse locations and their information.</p>

                {/* Filters Section */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title mb-3">
                      <i className="bi bi-funnel me-2"></i>
                      Filters
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-10">
                        <label htmlFor="search" className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          name="search"
                          placeholder="Name or Location"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={clearFilters}
                          title="Clear filters"
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Warehouses Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading warehouses...</p>
                  </div>
                ) : filteredWarehouses.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">No warehouses found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Warehouse Name</th>
                          <th>Location</th>
                          <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedWarehouses.map((warehouse) => (
                          <tr key={warehouse.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                  <i className="bi bi-building"></i>
                                </div>
                                <div className="fw-semibold">{warehouse.name}</div>
                              </div>
                            </td>
                            <td>
                              <span className="text-muted">
                                {warehouse.location || <span className="text-muted">-</span>}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => showWarehouseDetails(warehouse)}
                                title="View Details"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      itemLabel="warehouses"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageWarehouses

